import { styled } from '@linaria/react';
import { Group, Tabs } from '@mantine/core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useAdherenceStore } from '../../store/adherence-store/adherence-store';
import { AdherenceTab } from '../../store/adherence-store/adherence-store.type';
import { useGlobalStore } from '../../store/global-store/global-store';
import { baseWhite, inkLight, secondaryBase, skyLightest } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { H2 } from '../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { MissedEvents } from './missed-events/missed-events';
import { RiskAnalysis } from './risk-analysis/risk-analysis';
import { Targets } from './targets/targets';
import { appConfig } from '../../helpers/app-config/app-config';

const FLORA_MODULE_ENABLED = appConfig.VITE_FEATURE_FLAG_FLORA_MODULE === 'true';

export function Adherence() {
  const i18n = useTranslation();
  useDocumentTitle('Adherence - Bloomfilter');
  const selectedTab = useAdherenceStore((state) => state.selectedTab);
  const setSelectedTab = useAdherenceStore((state) => state.setSelectedTab);
  const useAI = useGlobalStore((state) => state.useAI);

  // Automatically switch to Targets tab when AI is disabled and Risk Analysis tab is selected
  useEffect(() => {
    if (!useAI && selectedTab === AdherenceTab.RISK_ANALYSIS) {
      setSelectedTab(AdherenceTab.TARGETS);
    }
  }, [useAI, selectedTab, setSelectedTab]);

  const header = (
    <Group>
      <Icon name="emergency" color={secondaryBase} size={36} />
      <H2>Adherence</H2>
      <StyledTabs
        variant="pills"
        radius="xl"
        value={selectedTab}
        onChange={(value) => setSelectedTab(value as AdherenceTab)}
      >
        <Tabs.List>
          {useAI && (
            <Tabs.Tab
              value={AdherenceTab.RISK_ANALYSIS}
              leftSection={
                <Icon
                  name="warning"
                  size={16}
                  color={selectedTab === AdherenceTab.RISK_ANALYSIS ? baseWhite : inkLight}
                />
              }
            >
              Risk Analysis
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value={AdherenceTab.TARGETS}
            leftSection={
              <Icon name="ads_click" size={16} color={selectedTab === AdherenceTab.TARGETS ? baseWhite : inkLight} />
            }
          >
            {i18n.t('common.targets')}
          </Tabs.Tab>
          {FLORA_MODULE_ENABLED && (
            <Tabs.Tab
              value={AdherenceTab.MISSED_EVENTS}
              leftSection={
                <Icon
                  name="alarm"
                  size={16}
                  color={selectedTab === AdherenceTab.MISSED_EVENTS ? baseWhite : inkLight}
                />
              }
            >
              Missed Events
            </Tabs.Tab>
          )}
        </Tabs.List>
      </StyledTabs>
    </Group>
  );

  return (
    <MenuDrivenContainer header={header}>
      {selectedTab === AdherenceTab.RISK_ANALYSIS && useAI && <RiskAnalysis />}
      {selectedTab === AdherenceTab.TARGETS && <Targets />}
      {selectedTab === AdherenceTab.MISSED_EVENTS && <MissedEvents />}
    </MenuDrivenContainer>
  );
}

export const StyledTabs = styled(Tabs)`
  .mantine-Tabs-list {
    gap: 8px;
  }

  .mantine-Tabs-tab {
    transition: all 0.2s ease;
    font-weight: 500;

    &[data-active] {
      color: ${baseWhite};
      background: ${secondaryBase};
    }

    &:not([data-active]) {
      color: ${inkLight};
      border: 1px solid ${inkLight};

      &:hover {
        background: ${skyLightest};
        border: 1px solid ${secondaryBase};
      }
    }
  }
`;
